import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { CrewProvider, useCrew } from "../lib/crew";
import { CrewBar } from "../components/CrewBar";
import { CrewPicker } from "../components/CrewPicker";
import { TabBar } from "../components/TabBar";
import { BlobField } from "../components/BlobField";
import { JoinByLink } from "../components/JoinByLink";

// Wraps every screen that belongs to a crew. It answers "am I in this crew?"
// once, so no screen below has to ask, and so the two unhappy answers get a real
// screen instead of an empty one.
// Shown wherever someone turns out to belong to nothing yet. Shared so the
// landing route and the crew gate cannot drift into two different answers to
// the same question.
export function NoCrews() {
  const navigate = useNavigate();
  return (
    <div className="app">
      <div className="header"><h1>No crews yet</h1></div>
      <p className="muted">
        If somebody sent you a link, paste it here and we&apos;ll get you in.
      </p>
      <JoinByLink />
      <button className="btn ghost" onClick={() => navigate("/new-league")}>
        Or start your own crew
      </button>
    </div>
  );
}

function CrewGate() {
  const { status, crews } = useCrew();
  const navigate = useNavigate();

  if (status === "loading") {
    return <div className="app"><div className="loading">Loading…</div></div>;
  }

  // A link to a crew they are not in. Usually a forwarded link, or the wrong
  // email. Both are worth saying out loud: silently redirecting them somewhere
  // else makes it look like the link was broken.
  if (status === "not-a-member") {
    return (
      <div className="app">
        <div className="header"><h1>Not your crew</h1></div>
        <div className="card">
          <span className="tag">You&apos;re not in this one</span>
          <p>
            This link points to a crew you haven&apos;t joined. If a friend sent it, ask them
            for an invite link instead. A crew link on its own won&apos;t let you in.
          </p>
          <p className="muted">
            If you were invited, check that you signed in with the same email the invite
            went to.
          </p>
        </div>
        {crews.length > 0 && (
          <button className="btn primary" onClick={() => navigate("/")}>
            Go to your {crews.length === 1 ? "crew" : "crews"}
          </button>
        )}
      </div>
    );
  }

  if (status === "no-crews") return <NoCrews />;

  return <Outlet />;
}

export function CrewLayout() {
  return (
    <CrewProvider>
      <CrewGate />
    </CrewProvider>
  );
}

// The everyday chrome: which crew you are in on top, the four tabs at the
// bottom, the screen in between.
//
// Only the everyday screens sit inside this. A live drop and a reveal are
// deliberately left bare: a drop is a timer running against you and a reveal is
// the payoff, and neither wants a navigation bar inviting you to leave halfway
// through. The host page and onboarding are one-off destinations reached from
// inside a tab, so they keep their own back link instead of a second one.
export function TabbedLayout() {
  const { crewId } = useCrew();
  const [picking, setPicking] = useState(false);
  if (!crewId) return null;

  return (
    <div className="shell">
      {/* The light the glass sits on. Fixed behind every everyday screen, so
          the cards have something to be translucent against. */}
      <BlobField />
      <CrewBar onOpenSwitcher={() => setPicking(true)} />
      <div className="shell-body">
        <Outlet />
      </div>
      <TabBar crewId={crewId} />
      <CrewPicker open={picking} onClose={() => setPicking(false)} />
    </div>
  );
}
